/**
 * 公共费用分摊的共用计算口径（预览与提交共用同一套，避免两处口径分叉）。
 *
 * 规则（与后端「按航班日取汇率」一致）：
 *  - 原币按分摊规则拆到各航段（平均分摊尾差加在最后一段；BH×单价 = 该段BH × 原币单价）。
 *  - 各段美元 = 该段原币 × 该段航班日的汇率（USD 固定 1.0）。
 *  - 某段缺航班日汇率 → 该段 allocatedUsd / rate 为 null，预览标「缺哪一天」，提交门禁拦下。
 */

/** 四舍五入到分 */
export function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * @param {object} costData  费用表单数据（含 allocationMethod / currency / amount / unitPrice）
 * @param {Array}  selectedLegs  已选航段（含 id / flightDate / blockHours）
 * @param {object} rateValidation 汇率验证结果（validLegs 含 { leg_id, rate, exchange_rate_id }）
 * @returns {{ results: Array, totalOriginal: number, totalUsd: number|null }}
 */
export function computeAllocation({ costData, selectedLegs, rateValidation }) {
    if (!costData || !selectedLegs || selectedLegs.length === 0) {
        return { results: [], totalOriginal: 0, totalUsd: null };
    }

    const isBhMode = costData.allocationMethod === "BY_BLOCK_HOURS";
    const currency = costData.currency || "USD";
    const isUsd = currency === "USD";

    const rateByLegId = {};
    if (rateValidation && Array.isArray(rateValidation.validLegs)) {
        rateValidation.validLegs.forEach((v) => {
            rateByLegId[v.leg_id] = v.rate;
        });
    }

    const totalBH = selectedLegs.reduce((sum, leg) => sum + (parseFloat(leg.blockHours) || 0), 0);
    const count = selectedLegs.length;

    const results = selectedLegs.map((leg, idx) => {
        const rate = isUsd ? 1.0 : (rateByLegId[leg.id] ?? null);

        let allocatedOriginal;
        let ratio;
        if (isBhMode) {
            const bh = parseFloat(leg.blockHours) || 0;
            allocatedOriginal = bh * (costData.unitPrice || 0);
            ratio = totalBH > 0 ? bh / totalBH : 0;
        } else {
            const baseOriginal = Math.floor((costData.amount || 0) / count * 100) / 100;
            allocatedOriginal = idx === count - 1
                ? round2((costData.amount || 0) - baseOriginal * (count - 1))
                : baseOriginal;
            ratio = 1 / count;
        }

        const original = round2(allocatedOriginal);
        const usd = rate != null ? round2(original * rate) : null;

        return { ...leg, ratio, allocatedOriginal: original, allocatedUsd: usd, rate };
    });

    const totalOriginal = round2(results.reduce((s, r) => s + r.allocatedOriginal, 0));
    const missingRate = results.some((r) => r.allocatedUsd == null);
    const totalUsd = missingRate ? null : round2(results.reduce((s, r) => s + r.allocatedUsd, 0));

    return { results, totalOriginal, totalUsd };
}

/**
 * 由各段结果汇总发票头「汇率」：各段汇率全相同 → 该值；否则 = 美元总额 ÷ 原币总额（折算值）。
 */
export function summarizeHeaderRate(results, totalOriginal, totalUsd) {
    const rates = results.filter((r) => r.rate != null).map((r) => r.rate);
    if (rates.length > 0 && rates.every((r) => r === rates[0])) {
        return rates[0];
    }
    if (totalOriginal && totalOriginal !== 0 && totalUsd != null) {
        return round2((totalUsd / totalOriginal) * 1000000) / 1000000;
    }
    return 1.0;
}
